import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import isEqual from "lodash/isEqual";

import { QUERY_STATUS } from "constants/constants";
import { extractApiFieldErrorsFlat } from "utils/apiError";
import {
  createJobCategoryQuery,
  deleteJobCategoryByIdQuery,
  getJobCategoryByIdQuery,
  getJobCategoryListQuery,
  updateJobCategoryByIdQuery,
} from "./queries";

// Kept out of the component so the state updaters below stay within the nesting
// limit, and so the list transforms read on their own.
const replaceById = (list, updated) =>
  list.map((elm) => (elm.id === updated.id ? updated : elm));

const removeById = (list, id) => list.filter((elm) => elm.id !== id);

const JobCategoryContext = createContext({});

export const useJobCategoryContext = () => useContext(JobCategoryContext);

export const JobCategoryProvider = ({ ...props }) => {
  const [jobCategoryList, setJobCategoryList] = useState([]);
  const [jobCategoryPerId, setJobCategoryPerId] = useState({});
  const [jobCategoryStatus, setJobCategoryStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [apiErrorResponse, setApiErrorResponse] = useState(null);

  // Always overwrites apiErrorResponse, including with null: a failure without
  // an exploitable body must not leave the previous business message in place.
  const handleError = useCallback((name, e) => {
    const errors = e?.response?.data?.["bad-request"];
    setApiErrorResponse(errors ? extractApiFieldErrorsFlat(errors) : null);
    setJobCategoryStatus(QUERY_STATUS.ERROR);
    console.error(name, e);
  }, []);

  const runMutation = useCallback(
    async (name, mutation) => {
      setApiErrorResponse(null);
      setJobCategoryStatus(QUERY_STATUS.LOADING);
      try {
        const result = await mutation();
        setJobCategoryStatus(QUERY_STATUS.SUCCESS);
        return result;
      } catch (e) {
        handleError(name, e);
        throw e;
      }
    },
    [handleError]
  );

  // Unlike JobProvider, the list is always refetched: the getter identity is
  // stable so screens still trigger a single call, but a stale list is never
  // served silently.
  const jobCategoryListGetter = useCallback(async () => {
    try {
      const list = await getJobCategoryListQuery();
      // Keeps the previous array when the payload is unchanged: every derived
      // map, getter and consumer context is keyed on this identity, so writing
      // an equal-but-new array would re-render the whole tree for nothing.
      setJobCategoryList((old) => (isEqual(old, list) ? old : list));
    } catch (e) {
      handleError("jobCategoryListGetter", e);
    }
  }, [handleError]);

  // Does not rethrow: the only caller is a mount effect, which has nowhere to
  // catch it.
  const jobCategoryByIdGetter = useCallback(
    async (id) => {
      try {
        setJobCategoryPerId(await getJobCategoryByIdQuery(id));
      } catch (e) {
        handleError("jobCategoryByIdGetter", e);
      }
    },
    [handleError]
  );

  const jobCategoryCreate = useCallback(
    (data) =>
      runMutation("jobCategoryCreate", async () => {
        const jobCategory = await createJobCategoryQuery(data);
        setJobCategoryList((old) => [...old, jobCategory]);
        return jobCategory;
      }),
    [runMutation]
  );

  const jobCategoryUpdateById = useCallback(
    (id, data) =>
      runMutation("jobCategoryUpdateById", async () => {
        const updated = await updateJobCategoryByIdQuery(id, data);
        setJobCategoryList((old) => replaceById(old, updated));
        setJobCategoryPerId(updated);
        return updated;
      }),
    [runMutation]
  );

  const jobCategoryDeleteById = useCallback(
    (id) =>
      runMutation("jobCategoryDeleteById", async () => {
        await deleteJobCategoryByIdQuery(id);
        setJobCategoryList((old) => removeById(old, id));
      }),
    [runMutation]
  );

  // categoryId -> category. Resolves a label without re-scanning the list.
  const jobCategoryById = useMemo(
    () =>
      jobCategoryList.reduce((acc, jobCategory) => {
        acc[jobCategory.id] = jobCategory;
        return acc;
      }, {}),
    [jobCategoryList]
  );

  // jobId -> its category. SINGULAR since redmine 12940: a job belongs to exactly
  // one category, so the category of any line carrying a job is fully determined
  // by that job. Every screen reads the category from here instead of from a
  // column the line used to copy.
  const jobCategoryByJobId = useMemo(
    () =>
      jobCategoryList.reduce((acc, jobCategory) => {
        (jobCategory?.jobs || []).forEach((jobId) => {
          acc[jobId] = jobCategory;
        });
        return acc;
      }, {}),
    [jobCategoryList]
  );

  // categoryId -> job ids. Feeds the Category -> Job cascade: the entry screens
  // restrict their job options to the selected category.
  const jobIdsByCategoryId = useMemo(() => {
    const acc = {};
    jobCategoryList.forEach((jobCategory) => {
      acc[jobCategory.id] = jobCategory?.jobs || [];
    });
    return acc;
  }, [jobCategoryList]);

  const getCategoryJobIds = useCallback(
    (categoryId) => jobIdsByCategoryId[categoryId] || [],
    [jobIdsByCategoryId]
  );

  // Several categories at once, for the multi-valued filters. Deduplicated: a
  // job can belong to more than one of them.
  const getCategoriesJobIds = useCallback(
    (categoryIds) => [
      ...new Set(
        (categoryIds || []).flatMap((id) => jobIdsByCategoryId[id] || [])
      ),
    ],
    [jobIdsByCategoryId]
  );

  // The category of a job. Never a guess: the referential holds exactly one per
  // job, so the entry screens fill the field in rather than asking for it.
  const getJobCategoryId = useCallback(
    (jobId) => jobCategoryByJobId[jobId]?.id ?? null,
    [jobCategoryByJobId]
  );

  const values = useMemo(
    () => ({
      apiErrorResponse,
      getCategoriesJobIds,
      getCategoryJobIds,
      getJobCategoryId,
      jobCategoryById,
      jobCategoryByIdGetter,
      jobCategoryByJobId,
      jobCategoryCreate,
      jobCategoryDeleteById,
      jobCategoryList,
      jobCategoryListGetter,
      jobCategoryPerId,
      jobCategoryStatus,
      jobCategoryUpdateById,
      setJobCategoryStatus,
    }),
    [
      apiErrorResponse,
      getCategoriesJobIds,
      getCategoryJobIds,
      getJobCategoryId,
      jobCategoryById,
      jobCategoryByIdGetter,
      jobCategoryByJobId,
      jobCategoryCreate,
      jobCategoryDeleteById,
      jobCategoryList,
      jobCategoryListGetter,
      jobCategoryPerId,
      jobCategoryStatus,
      jobCategoryUpdateById,
    ]
  );

  return <JobCategoryContext.Provider value={values} {...props} />;
};
